/**
 * Partager une fiche du coffre par lien (« Share item » de 1Password, `psst.1password.com`) : la fiche part comme un
 * envoi ponctuel (`R15`, `send.ts`) de nature `item` -- chiffree sur cet appareil, cle dans le fragment `#` du lien,
 * expiration, nombre de telechargements et mot de passe facultatif inchanges. Le destinataire n'a pas besoin de
 * compte et voit les champs un par un, les secrets masques a l'ouverture.
 *
 * Ce qui est partageable : identifiant, mot de passe, adresses, champs types (carte, identite, Wi-Fi…), champs
 * personnalises et notes -- chacun cochable, tous coches par defaut. Ce qui ne part JAMAIS, quoi qu'on coche :
 *   - la graine TOTP : le lien donnerait un second facteur PERMANENT, qui survit a l'expiration du lien ;
 *   - l'historique des mots de passe et les versions : c'est le passe du compte, pas ce qu'on partage ;
 *   - les passkeys, la cle privee SSH et les pieces jointes : une cle se delegue, elle ne se recopie pas.
 *
 * La valeur de chaque champ est lue par `entryFieldValue` -- la MEME lecture que « Copier » -- au moment de creer le
 * lien : le formulaire ne porte que des references (`data-field`), jamais une valeur. Une reference qui n'est pas
 * dans `shareableFields` (forgee, `history:0`, `version:…`) est ignoree. Module pur, zero E/S.
 */
import type { EntrySecrets, SendItemField, SendPayload } from '@klavox/crypto-core';
import { checkboxLabel, isCheckboxField, readCustomFields } from './custom-fields.js';
import { entryFieldValue } from './entry-copy.js';
import { TYPED_FIELD_GROUPS, fieldSpecs, readTypedFields } from './typed-fields.js';

/** Un champ partageable de la fiche : sa reference (celle de « Copier »), son libelle, et s'il est secret. */
export interface ShareableField {
  readonly ref: string;
  readonly label: string;
  readonly secret: boolean;
}

/** Reference des notes : absente d'`entry-copy.ts` (les notes ne se copient pas d'un bouton), propre au partage. */
export const NOTES_REF = 'notes';

/** Champs partageables d'une fiche, dans l'ordre de la fiche ; un champ vide n'y figure pas. */
export function shareableFields(secrets: EntrySecrets): ShareableField[] {
  const fields: ShareableField[] = [];
  if (entryFieldValue(secrets, 'username') !== null) fields.push({ ref: 'username', label: 'Identifiant', secret: false });
  if (entryFieldValue(secrets, 'password') !== null) fields.push({ ref: 'password', label: 'Mot de passe', secret: true });
  for (const group of TYPED_FIELD_GROUPS) {
    const values = readTypedFields(secrets, group);
    for (const spec of fieldSpecs(group)) {
      if (Object.hasOwn(values, spec.key)) fields.push({ ref: `typed:${group}:${spec.key}`, label: spec.label, secret: spec.sensitive === true });
    }
  }
  readCustomFields(secrets).forEach((field, index) => {
    if (field.value.length > 0) fields.push({ ref: `custom:${index}`, label: field.name, secret: field.hidden === true });
  });
  const uris = Array.isArray(secrets.uris) ? secrets.uris : [];
  uris.forEach((uri, index) => {
    if (typeof uri === 'string' && uri.length > 0) fields.push({ ref: `uri:${index}`, label: index === 0 ? 'Adresse' : `Adresse ${index + 1}`, secret: false });
  });
  if (typeof secrets.notes === 'string' && secrets.notes.length > 0) fields.push({ ref: NOTES_REF, label: 'Notes', secret: false });
  return fields;
}

/** Valeur partagee d'un champ : celle de « Copier », une case a cocher dite en clair, les notes telles quelles. */
function sharedValue(secrets: EntrySecrets, ref: string): string | null {
  if (ref === NOTES_REF) return typeof secrets.notes === 'string' && secrets.notes.length > 0 ? secrets.notes : null;
  const value = entryFieldValue(secrets, ref);
  if (value === null || !ref.startsWith('custom:')) return value;
  const field = readCustomFields(secrets)[Number(ref.slice('custom:'.length))];
  return field !== undefined && isCheckboxField(field) ? checkboxLabel(value) : value;
}

/**
 * Contenu de l'envoi d'une fiche : seuls les champs partageables ET choisis, dans l'ordre de la fiche. `null` si
 * aucun champ ne reste -- un lien vide ne se cree pas.
 */
export function buildEntrySharePayload(secrets: EntrySecrets, label: string, chosen: ReadonlySet<string>): SendPayload | null {
  const fields: SendItemField[] = [];
  for (const field of shareableFields(secrets)) {
    if (!chosen.has(field.ref)) continue;
    const value = sharedValue(secrets, field.ref);
    if (value === null) continue;
    fields.push(field.secret ? { name: field.label, value, secret: true } : { name: field.label, value });
  }
  return fields.length === 0 ? null : { kind: 'item', label, fields };
}
